/**
 * axios setup with token renewal mechanism
 */

import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { tokenManager } from './tokenManager';

const axiosServices = axios.create();

// Interceptor para agregar el token de autorización a las peticiones
axiosServices.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
        try {
            const token = await tokenManager.getValidToken();
            if (token) {
                config.headers = {
                    ...config.headers,
                    Authorization: `Bearer ${token}`
                };
            }
        } catch (error) {
            // Si no se puede obtener un token válido, continuar sin autorización
            // El interceptor de respuesta manejará el error 401
            console.warn('No se pudo obtener un token válido:', error);
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Interceptor para manejar errores de autenticación y renovación de tokens
axiosServices.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Si es un error 401 y no hemos intentado renovar el token
        // eslint-disable-next-line no-underscore-dangle
        if (error.response?.status === 401 && !originalRequest._retry) {
            // eslint-disable-next-line no-underscore-dangle
            originalRequest._retry = true;

            try {
                // Intentar renovar el token usando GraphQL
                const newToken = await tokenManager.refreshAccessToken();
                // Actualizar el header de autorización
                if (originalRequest.headers) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                }

                // Reintentar la petición original
                // eslint-disable-next-line @typescript-eslint/return-await
                return axiosServices(originalRequest);
            } catch (refreshError) {
                console.error('Error renovando token:', refreshError);
                // Si la renovación falla, limpiar tokens y redirigir al login
                tokenManager.clearTokens();

                // Disparar evento personalizado para notificar a la aplicación
                window.dispatchEvent(new CustomEvent('auth:token-expired'));

                return Promise.reject(refreshError);
            }
        }

        // Para otros errores, devolver el error original
        return Promise.reject((error.response && error.response.data) || 'Wrong Services');
    }
);

export default axiosServices;
